import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Search, MapPin, Calendar, ShieldCheck, Star, Sparkles, Video, X, Send, Bot, Minimize2, Maximize2, Info, Clock, Phone, Wallet, FlaskConical } from "lucide-react";
import heroDoctor from "../assets/hero-doctor.jpg";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../PageRouting/pageRoutes";
import UserService from "../Services/UserService";

const SPECIALTIES = [
  "General Physician", "Dermatologist", "Cardiologist",
  "Pediatrician", "Gynecologist", "Dentist",
];

const BRANDS = [
  "NHS", "Mayo Clinic", "Cleveland Clinic", "Bupa",
  "Kaiser Permanente", "AXA Health", "Cigna", "Stripe Health",
];

/* ─── Token maps ─────────────────────────────────────────── */
const LIGHT = {
  sectionBg: "linear-gradient(135deg, #e0f5f2 0%, #cdeef8 35%, #c4ecf8 65%, #e4f7f3 100%)",
  gridLine: "rgba(0,150,140,0.07)",
  glow1: "rgba(0,180,160,0.22)",
  glow2: "rgba(0,200,220,0.18)",
  badgeBg: "rgba(255,255,255,0.75)",
  badgeBorder: "rgba(0,150,140,0.25)",
  badgeText: "#4a6070",
  badgeDot: "#0ba88a",
  h1Color: "#0a1628",
  accentWord: "#12c4b4",
  descColor: "#5a6e80",
  searchBoxBg: "#ffffff",
  searchBoxBorder: "rgba(0,150,140,0.18)",
  searchBoxShadow: "0 8px 32px rgba(0,150,140,0.12)",
  searchRowBg: "#f4f9f8",
  searchDivider: "#e2eceb",
  searchIconColor: "#1e4d6b",
  searchText: "#1e4d6b",
  searchBtnBg: "linear-gradient(135deg,#1e4d6b 0%,#0d2d45 100%)",
  searchBtnColor: "#ffffff",
  chipBorder: "#dde5ea",
  chipBg: "#ffffff",
  chipText: "#2d5e6a",
  chipHoverBorder: "#0ba88a",
  chipHoverText: "#0ba88a",
  popularLabel: "#6b7a8a",
  trustText: "#5a6e80",
  trustIconHipaa: "#0ba88a",
  floatBg: "rgba(255,255,255,0.92)",
  floatBorder: "rgba(255,255,255,0.95)",
  floatShadow: "0 8px 28px rgba(0,80,120,0.12)",
  floatSubText: "#8fa8b8",
  floatTitleText: "#1e3a5f",
  floatPatientText: "#1e3a5f",
  liveBg: "rgba(11,168,138,0.12)",
  liveText: "#0ba88a",
  liveDot: "#0ba88a",
  progressBg: "#e8f0ee",
  progressFill: "linear-gradient(90deg,#1e4d6b,#0ba88a)",
  avatarBorder: "#ffffff",
  avatarBg: "linear-gradient(135deg,#0ba88a,#3dd6c8)",
  marqueeText: "#7a9aaa",
  marqueeLabel: "rgba(13,29,60,0.28)",
  imageBorder: "rgba(255,255,255,0.8)",
  imageOverlay: "linear-gradient(to top,rgba(13,29,60,0.28),transparent)",

  /* DoxBot chatbot tokens (light mode) */
  fabBg: "linear-gradient(135deg,#1e4d6b 0%,#0ba88a 100%)",
  fabShadow: "0 8px 32px rgba(11,168,138,0.35)",
  fabRing: "rgba(11,168,138,0.20)",
  windowBg: "#ffffff",
  windowBorder: "rgba(0,150,140,0.18)",
  windowShadow: "0 24px 64px rgba(0,60,90,0.18), 0 4px 16px rgba(0,150,140,0.10)",
  headerBg: "linear-gradient(135deg,#1e4d6b 0%,#0d2d45 100%)",
  headerText: "#ffffff",
  headerSub: "rgba(255,255,255,0.75)",
  statusDot: "#3dd6c8",
  bodyBg: "#f4f9f8",
  inputAreaBg: "#ffffff",
  inputBorder: "rgba(0,150,140,0.18)",
  inputBg: "#f4f9f8",
  inputText: "#1e3a5f",
  sendBtnBg: "linear-gradient(135deg,#1e4d6b,#0ba88a)",
  sendBtnColor: "#ffffff",
  botBubbleBg: "#ffffff",
  botBubbleBorder: "rgba(0,150,140,0.15)",
  botBubbleText: "#1e3a5f",
  userBubbleBg: "linear-gradient(135deg,#1e4d6b,#0ba88a)",
  userBubbleText: "#ffffff",
  timestampColor: "#8fa8b8",
  dividerColor: "rgba(0,150,140,0.12)",
  suggestionBg: "#ffffff",
  suggestionBorder: "rgba(0,150,140,0.25)",
  suggestionText: "#1e4d6b",
  suggestionHoverBg: "#0ba88a",
  suggestionHoverText: "#ffffff",
  botAvatarBg: "linear-gradient(135deg,#0ba88a,#3dd6c8)",
  minimizeBg: "rgba(0,0,0,0.06)",
};

const DARK = {
  sectionBg: "linear-gradient(135deg, #0a0800 0%, #0f0b01 35%, #110d02 65%, #0a0800 100%)",
  gridLine: "rgba(200,150,30,0.07)",
  glow1: "rgba(200,150,30,0.15)",
  glow2: "rgba(240,192,64,0.10)",
  badgeBg: "rgba(17,13,2,0.85)",
  badgeBorder: "rgba(200,150,30,0.30)",
  badgeText: "#b89a4a",
  badgeDot: "#f0c040",
  h1Color: "#f5e6b0",
  accentWord: "#f0c040",
  descColor: "#b89a4a",
  searchBoxBg: "#110d02",
  searchBoxBorder: "rgba(200,150,30,0.25)",
  searchBoxShadow: "0 8px 32px rgba(200,150,30,0.10)",
  searchRowBg: "#0f0b01",
  searchDivider: "rgba(200,150,30,0.18)",
  searchIconColor: "#b89a4a",
  searchText: "#f5e6b0",
  searchBtnBg: "linear-gradient(135deg,#c8961e 0%,#7a5c10 100%)",
  searchBtnColor: "#0a0800",
  chipBorder: "rgba(200,150,30,0.25)",
  chipBg: "#110d02",
  chipText: "#b89a4a",
  chipHoverBorder: "#f0c040",
  chipHoverText: "#f0c040",
  popularLabel: "#7a5c10",
  trustText: "#b89a4a",
  trustIconHipaa: "#c8961e",
  floatBg: "rgba(17,13,2,0.92)",
  floatBorder: "rgba(200,150,30,0.30)",
  floatShadow: "0 8px 28px rgba(200,150,30,0.12)",
  floatSubText: "#7a5c10",
  floatTitleText: "#f5e6b0",
  floatPatientText: "#f5e6b0",
  liveBg: "rgba(200,150,30,0.12)",
  liveText: "#f0c040",
  liveDot: "#f0c040",
  progressBg: "rgba(200,150,30,0.12)",
  progressFill: "linear-gradient(90deg,#c8961e,#f0c040)",
  avatarBorder: "#110d02",
  avatarBg: "linear-gradient(135deg,#c8961e,#f0c040)",
  marqueeText: "#7a5c10",
  marqueeLabel: "rgba(200,150,30,0.30)",
  imageBorder: "rgba(200,150,30,0.30)",
  imageOverlay: "linear-gradient(to top,rgba(10,8,0,0.50),transparent)",
  fabBg: "linear-gradient(135deg,#c8961e 0%,#f0c040 100%)",
  fabShadow: "0 8px 32px rgba(200,150,30,0.45)",
  fabRing: "rgba(240,192,64,0.20)",
  windowBg: "#0f0b01",
  windowBorder: "rgba(200,150,30,0.28)",
  windowShadow: "0 24px 64px rgba(0,0,0,0.55), 0 4px 16px rgba(200,150,30,0.15)",
  headerBg: "linear-gradient(135deg,#7a5c10 0%,#c8961e 100%)",
  headerText: "#0a0800",
  headerSub: "rgba(10,8,0,0.65)",
  statusDot: "#0a0800",
  bodyBg: "#0a0800",
  inputAreaBg: "#0f0b01",
  inputBorder: "rgba(200,150,30,0.25)",
  inputBg: "#0a0800",
  inputText: "#f5e6b0",
  sendBtnBg: "linear-gradient(135deg,#c8961e,#f0c040)",
  sendBtnColor: "#0a0800",
  botBubbleBg: "#110d02",
  botBubbleBorder: "rgba(200,150,30,0.22)",
  botBubbleText: "#f5e6b0",
  userBubbleBg: "linear-gradient(135deg,#c8961e,#f0c040)",
  userBubbleText: "#0a0800",
  timestampColor: "#7a5c10",
  dividerColor: "rgba(200,150,30,0.10)",
  suggestionBg: "#110d02",
  suggestionBorder: "rgba(200,150,30,0.28)",
  suggestionText: "#f0c040",
  suggestionHoverBg: "#c8961e",
  suggestionHoverText: "#0a0800",
  botAvatarBg: "linear-gradient(135deg,#c8961e,#f0c040)",
  minimizeBg: "rgba(0,0,0,0.20)",
};

/* ── Hook: MutationObserver watches <html data-theme> ─────────────────── */
function useDarkMode() {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark"
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

/* ── Quick suggestions (Doxzon FAQ-style questions) ── */
const SUGGESTIONS = [
  { label: "What is Doxzon?", icon: Info },
  { label: "Are you available 24/7?", icon: Clock },
  { label: "Is my data secure?", icon: ShieldCheck },
  { label: "Do you have lab tests?", icon: FlaskConical },
  { label: "Contact support", icon: Phone },
];

/* ── Doxzon contact info (used as fallback for unknown queries) ── */
const SUPPORT_PHONE = "+254 712 XXX 678";
const SUPPORT_EMAIL = "support@doxzon.com";

/* ── Bot replies ── */
function getBotReply(msg) {
  const m = msg.toLowerCase();

  /* ── Doxzon company-related Q&A ── */
  if (m.includes("doxzon") && (m.includes("what") || m.includes("about") || m.includes("company")))
    return "Doxzon is a complete digital healthcare platform — connect with trusted doctors, diagnostics, medicines, and home care, all in one place. 🏥";

  if (m.includes("timing") || m.includes("hours") || m.includes("available") || m.includes("open"))
    return "Doxzon is available 24/7! You can book a doctor consultation, order medicines, or schedule a lab test anytime.";

  if (m.includes("secure") || m.includes("data") || m.includes("privacy") || m.includes("safe"))
    return "Yes, your data is fully secure with Doxzon. We're HIPAA compliant and use end-to-end encryption to protect your health information. 🔒";
  if (m.includes("contact") || m.includes("phone") || m.includes("number") || m.includes("email") || m.includes("support") || m.includes("help me") || m.includes("talk to someone"))
    return `You can reach our support team anytime at 📞 ${SUPPORT_PHONE} or 📧 ${SUPPORT_EMAIL} — we're happy to help!`;

  /* ── Existing intents ── */
  if (m.includes("book") || m.includes("doctor") || m.includes("consult"))
    return "I can help you book a doctor! 🩺 Please tell me your city and preferred specialty, or tap 'Consult Doctor' in the top menu.";
  if (m.includes("lab") || m.includes("test"))
    return "We offer 500+ lab tests with home sample collection. Would you like to browse tests or search by condition?";
  if (m.includes("prescri"))
    return "You can upload your prescription directly. Go to Medicine → Upload Prescription, and we'll process it within minutes.";
  if (m.includes("symptom") || m.includes("pain") || m.includes("fever"))
    return "I'm sorry to hear that. Our AI triage can help analyse your symptoms. Shall I connect you to a General Physician?";
  if (m.includes("hi") || m.includes("hello") || m.includes("hey"))
    return "Hello! 👋 I'm Doxbot, your Doxzon Ai health assistant. How can I help you today?";
  if (m.includes("thank"))
    return "You're welcome! 😊 Is there anything else I can help you with?";

  /* ── Fallback for anything Doxbot doesn't recognise ── */
  return ` For further assistance, please contact us at 📞 ${SUPPORT_PHONE} or 📧 ${SUPPORT_EMAIL}.`;
}

function getNow() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/* ── DoxBot Widget ── */
function DoxBot({ isDark, t }) {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [pulse, setPulse] = useState(true);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi! 👋 I'm **Doxbot**, your Doxzon Ai health assistant. How can I help you today?", time: getNow() },
  ]);
  const bottomRef = useRef(null);

  useEffect(() => { if (open) setPulse(false); }, [open]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing]);

  function sendMessage(text) {
    const msg = text || input.trim();
    if (!msg) return;
    setInput("");
    setMessages((p) => [...p, { role: "user", text: msg, time: getNow() }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((p) => [...p, { role: "bot", text: getBotReply(msg), time: getNow() }]);
    }, 900 + Math.random() * 500);
  }

  function renderText(text) {
    return text.split(/\*\*(.*?)\*\*/g).map((chunk, i) =>
      i % 2 === 1 ? <strong key={i}>{chunk}</strong> : chunk
    );
  }

  const fabColor = isDark ? "#0a0800" : "#ffffff";

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open Doxbot chat"
          className="dox-fab-pill"
          style={{
            position: "fixed",
            bottom: "28px",
            right: "28px",
            zIndex: 99999,
            width: "52px",
            height: "52px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            cursor: "pointer",
            borderRadius: "9999px",
            background: t.fabBg,
            boxShadow: isDark
              ? "0 10px 28px rgba(200,150,30,0.45), 0 0 0 1px rgba(200,150,30,0.15)"
              : "0 10px 28px rgba(11,168,138,0.35), 0 0 0 1px rgba(255,255,255,0.15)",
          }}
        >
          {pulse && <span className="doxGlow" style={{ background: isDark ? "#f0c040" : "#12c4b4" }} />}

          <Bot size={22} color={fabColor} strokeWidth={2.2} />

          <span
            aria-hidden
            className={pulse ? "dox-dot-blink" : ""}
            style={{
              position: "absolute",
              bottom: "2px",
              right: "2px",
              width: "12px",
              height: "12px",
              borderRadius: "9999px",
              background: "#2ee6a8",
              border: `2px solid ${isDark ? "#0f0b01" : "#ffffff"}`,
            }}
          />
        </button>
      )}
      {open && (
        <div style={{ position: "fixed", bottom: "28px", right: "28px", zIndex: 99999, width: "360px", borderRadius: "24px", background: t.windowBg, border: `1px solid ${t.windowBorder}`, boxShadow: t.windowShadow, display: "flex", flexDirection: "column", overflow: "hidden", maxHeight: minimized ? "64px" : "560px", transition: "max-height 0.35s cubic-bezier(0.4,0,0.2,1), background 0.3s, border-color 0.3s", animation: "doxSlideUp 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards" }}>
          <div style={{ background: t.headerBg, padding: "14px 16px", display: "flex", alignItems: "center", gap: "10px", flexShrink: 0, cursor: minimized ? "pointer" : "default" }} onClick={() => minimized && setMinimized(false)}>
            <div style={{ width: "38px", height: "38px", borderRadius: "12px", background: t.botAvatarBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 2px 8px rgba(0,0,0,0.20)" }}>
              <Sparkles size={18} color={isDark ? "#0a0800" : "#032d57"} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ fontSize: "14px", fontWeight: 700, color: t.headerText }}>Doxbot</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", background: "rgba(0,0,0,0.15)", borderRadius: "9999px", padding: "1px 7px", fontSize: "9px", fontWeight: 700, color: t.headerText, letterSpacing: "0.05em" }}>
                  <span style={{ width: "5px", height: "5px", borderRadius: "9999px", background: t.statusDot }} />ONLINE
                </span>
              </div>
              <p style={{ fontSize: "10px", color: t.headerSub, margin: 0, marginTop: "1px" }}>Doxzon AI Health Assistant · Replies instantly</p>
            </div>
            <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
              <button onClick={(e) => { e.stopPropagation(); setMinimized((p) => !p); }} style={{ width: "28px", height: "28px", borderRadius: "8px", border: "none", background: t.minimizeBg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {minimized ? <Maximize2 size={13} color={t.headerText} /> : <Minimize2 size={13} color={t.headerText} />}
              </button>
              <button onClick={() => setOpen(false)} style={{ width: "28px", height: "28px", borderRadius: "8px", border: "none", background: t.minimizeBg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={13} color={t.headerText} />
              </button>
            </div>
          </div>
          {!minimized && (
            <>
              <div style={{ flex: 1, overflowY: "auto", padding: "16px 14px", background: t.bodyBg, display: "flex", flexDirection: "column", gap: "10px", scrollbarWidth: "thin", scrollbarColor: `rgba(128,128,128,0.3) transparent` }}>
                {messages.map((msg, i) => (
                  <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start" }}>
                    <div style={{ maxWidth: "82%", background: msg.role === "user" ? t.userBubbleBg : t.botBubbleBg, border: msg.role === "bot" ? `1px solid ${t.botBubbleBorder}` : "none", borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px", padding: "9px 13px", fontSize: "13px", lineHeight: 1.55, color: msg.role === "user" ? t.userBubbleText : t.botBubbleText }}>
                      {renderText(msg.text)}
                    </div>
                    <span style={{ fontSize: "9px", color: t.timestampColor, marginTop: "3px", padding: "0 4px" }}>{msg.time}</span>
                  </div>
                ))}
                {typing && (
                  <div style={{ display: "flex", alignItems: "center", gap: "4px", padding: "10px 13px", background: t.botBubbleBg, border: `1px solid ${t.botBubbleBorder}`, borderRadius: "18px 18px 18px 4px", width: "fit-content" }}>
                    {[0, 0.18, 0.36].map((delay, i) => (
                      <span key={i} style={{ width: "7px", height: "7px", borderRadius: "9999px", background: isDark ? "#c8961e" : "#18c3ae", display: "inline-block", animation: `doxTyping 1.2s ${delay}s infinite ease-in-out` }} />
                    ))}
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
              <div style={{ padding: "10px 14px 6px", borderTop: `1px solid ${t.dividerColor}`, background: t.inputAreaBg, display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {SUGGESTIONS.map((s) => (
                  <button key={s.label} onClick={() => sendMessage(s.label)}
                    style={{ display: "flex", alignItems: "center", gap: "5px", border: `1px solid ${t.suggestionBorder}`, background: t.suggestionBg, borderRadius: "9999px", padding: "5px 12px 5px 8px", fontSize: "10.5px", color: t.suggestionText, cursor: "pointer", fontWeight: 500, transition: "all 0.15s", whiteSpace: "nowrap" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = t.suggestionHoverBg; e.currentTarget.style.color = t.suggestionHoverText; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = t.suggestionBg; e.currentTarget.style.color = t.suggestionText; }}
                  >
                    <s.icon size={12} strokeWidth={2.2} />
                    {s.label}
                  </button>
                ))}
              </div>
              <div style={{ padding: "10px 12px 14px", background: t.inputAreaBg, display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ flex: 1, display: "flex", alignItems: "center", background: t.inputBg, border: `1px solid ${t.inputBorder}`, borderRadius: "14px", padding: "0 12px" }}>
                  <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder="Type a message…" style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: "13px", color: t.inputText, padding: "10px 0" }} />
                </div>
                <button onClick={() => sendMessage()} disabled={!input.trim()} style={{ width: "40px", height: "40px", borderRadius: "12px", background: input.trim() ? t.sendBtnBg : (isDark ? "rgba(200,150,30,0.15)" : "rgba(3,45,87,0.08)"), border: "none", cursor: input.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s" }}>
                  <Send size={15} color={input.trim() ? t.sendBtnColor : (isDark ? "#7a5c10" : "#9ca3af")} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

export function Hero() {
  const isDark = useDarkMode();
  const t = isDark ? DARK : LIGHT;
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [cityQuery, setCityQuery] = useState("");

  // Fetch matching symptom codes from the API (uses same base URL as other services)
  const fetchSymptomCode = async (symptom) => {
    try {
      const response = await UserService.getSymptomsByName({ symptomName: symptom, status: 1 });
      const list = response?.data?.data || [];
      if (list.length > 0) return list[0].symptomCode;
    } catch (e) {
      console.error("fetchSymptomCode failed", e);
    }
    return null;
  };

  const handleSearch = async () => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      const code = await fetchSymptomCode(searchQuery.trim());
      if (code) {
        params.set("symptomsCodes", code);
      } else {
        params.set("q", searchQuery.trim());
      }
    }
    if (cityQuery.trim()) {
      const cleaned = cityQuery.trim();
      if (/^\d+$/.test(cleaned)) {
        params.set("pincode", cleaned);
      } else {
        params.set("city", cleaned);
      }
    }
    const qs = params.toString();
    navigate(`${APP_ROUTES.CONSULTDOCTOR}${qs ? `?${qs}` : ""}`);
  };

  const handleChipClick = (specialityName) => {
    const params = new URLSearchParams();
    params.set("speciality", specialityName);
    navigate(`${APP_ROUTES.CONSULTDOCTOR}?${params.toString()}`);
  };

  return (
    <>
      <section
        id="top"
        style={{
          background: t.sectionBg,
          position: "relative",
          isolation: "isolate",
          overflow: "hidden",
          paddingTop: "7rem",
          paddingBottom: "6rem",
          transition: "background 0.3s",
        }}
      >
        {/* Grid bg */}
        <div aria-hidden style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: `linear-gradient(${t.gridLine} 1px,transparent 1px),linear-gradient(90deg,${t.gridLine} 1px,transparent 1px)`,
          backgroundSize: "32px 32px", opacity: 0.6,
        }} />

        {/* Glow blobs */}
        <div aria-hidden style={{ position: "absolute", top: "-8rem", right: 0, height: "480px", width: "480px", borderRadius: "9999px", background: `radial-gradient(circle,${t.glow1},transparent 70%)`, filter: "blur(60px)", pointerEvents: "none" }} />
        <div aria-hidden style={{ position: "absolute", bottom: "-10rem", left: "-8rem", height: "420px", width: "420px", borderRadius: "9999px", background: `radial-gradient(circle,${t.glow2},transparent 70%)`, filter: "blur(60px)", pointerEvents: "none" }} />

        {/* Two-column → single on mobile via CSS class */}
        <div className="hero-grid">

          {/* ── LEFT ── */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                borderRadius: "9999px", border: `1px solid ${t.badgeBorder}`,
                background: t.badgeBg, backdropFilter: "blur(8px)",
                padding: "5px 14px", fontSize: "11px", fontWeight: 500,
                color: t.badgeText, marginBottom: "1rem",
              }}
            >
              <span style={{ width: "7px", height: "7px", borderRadius: "9999px", background: t.badgeDot, display: "inline-block", animation: "pulse 2s infinite" }} />
              Trusted by clinicians
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="hero-h1"
              style={{
                fontFamily: "'Sora','Plus Jakarta Sans',system-ui,sans-serif",
                fontWeight: 500, lineHeight: 1.05, letterSpacing: "-2px",
                color: t.h1Color, marginBottom: "1rem",
              }}
            >
              Your Complete{" "}
              <span style={{ color: t.accentWord, fontWeight: 400 }}>Digital Healthcare</span>
              <br />Ecosystem
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="hero-desc"
              style={{ color: t.descColor, lineHeight: 1.65, marginBottom: "1.75rem" }}
            >
              Connect with Our trusted  doctors, diagnostics, medicines, and Home care — all in one platform.
            </motion.p>

            {/* Search Box */}
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.18 }}
              style={{
                background: t.searchBoxBg, borderRadius: "20px",
                border: `1px solid ${t.searchBoxBorder}`,
                boxShadow: t.searchBoxShadow, padding: "10px",
                transition: "background 0.3s, border-color 0.3s",
              }}
            >
              {/* Search row → stacks on mobile */}
              <div className="search-row" style={{ background: t.searchRowBg, borderRadius: "12px", overflow: "hidden", marginBottom: "10px" }}>

                {/* Symptom input */}
                <div className="search-field" style={{ borderRight: `1px solid ${t.searchDivider}` }}>
                  <Search size={14} color={t.searchIconColor} style={{ flexShrink: 0 }} />
                  <input
                    className="search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    style={{ background: "transparent", border: "none", flex: 1, minWidth: 0, outline: "none", color: t.searchText }}
                    placeholder="Search"
                  />
                </div>

                {/* City input */}
                <div className="search-field search-field--city" style={{ borderRight: `1px solid ${t.searchDivider}` }}>
                  <MapPin size={14} color={t.searchIconColor} style={{ flexShrink: 0 }} />
                  <input
                    className="search-input"
                    value={cityQuery}
                    onChange={(e) => setCityQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    style={{ background: "transparent", border: "none", outline: "none", color: t.searchText }}
                    placeholder="City or Pincode"
                  />
                </div>

                {/* Date select + Find button row on mobile */}
                <div className="search-bottom-row">
                  <div className="search-field search-field--date">
                    <Calendar size={14} color={t.searchIconColor} style={{ flexShrink: 0 }} />
                  </div>
                  <button
                    className="search-btn"
                    onClick={handleSearch}
                    style={{ background: t.searchBtnBg, color: t.searchBtnColor }}
                    onMouseOver={(e) => (e.currentTarget.style.opacity = "0.88")}
                    onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
                  >
                    Find a doctor
                  </button>
                </div>
              </div>

              {/* Chips */}
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", padding: "2px" }}>
                <span style={{ fontSize: "11px", fontWeight: 500, color: t.popularLabel }}>Popular:</span>
                {SPECIALTIES.map((s) => (
                  <button key={s}
                    onClick={() => handleChipClick(s)}
                    style={{ border: `1px solid ${t.chipBorder}`, background: t.chipBg, borderRadius: "9999px", padding: "3px 10px", fontSize: "11px", color: t.chipText, cursor: "pointer", transition: "all .15s" }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = t.chipHoverBorder; e.currentTarget.style.color = t.chipHoverText; }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = t.chipBorder; e.currentTarget.style.color = t.chipText; }}
                  >{s}</button>
                ))}
              </div>
            </motion.div>

            {/* Trust badges */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem", marginTop: "1.25rem", fontSize: "12px", color: t.trustText }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <ShieldCheck size={14} color={t.trustIconHipaa} />
                HIPAA Compliant
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Star size={14} fill="#f5a623" color="#f5a623" />
                4.9/5 from 28k reviews
              </div>
            </div>
          </div>

          {/* ── RIGHT – hidden on mobile ── */}
          <div className="hero-right" style={{ position: "relative" }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}
              style={{ aspectRatio: "4/5", borderRadius: "2.5rem", overflow: "hidden", border: `1px solid ${t.imageBorder}`, boxShadow: "0 20px 60px rgba(0,100,120,0.18)", position: "relative" }}
            >
              <img src={heroDoctor} alt="Doctor on a video consultation" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <div style={{ position: "absolute", inset: 0, background: t.imageOverlay }} />
            </motion.div>

            {/* Float: Live consultation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
              style={{ position: "absolute", left: "-1.5rem", top: "2rem", width: "240px", borderRadius: "18px", background: t.floatBg, backdropFilter: "blur(14px)", border: `1px solid ${t.floatBorder}`, boxShadow: t.floatShadow, padding: "12px 14px", animation: "floatY 6s ease-in-out infinite", transition: "background 0.3s, border-color 0.3s" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "38px", height: "38px", borderRadius: "12px", background: "linear-gradient(135deg,#1e4d6b,#0d2d45)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Video size={16} color="#fff" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: "10px", color: t.floatSubText, margin: 0 }}>Live consultation</p>
                  <p style={{ fontSize: "11.5px", fontWeight: 600, color: t.floatTitleText, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Dr. Emily Carter · General Practice</p>
                </div>
                <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: "3px", background: t.liveBg, color: t.liveText, borderRadius: "9999px", padding: "2px 7px", fontSize: "9px", fontWeight: 700, flexShrink: 0 }}>
                  <span style={{ width: "5px", height: "5px", borderRadius: "9999px", background: t.liveDot, animation: "pulse 2s infinite" }} />
                  LIVE
                </span>
              </div>
            </motion.div>

            {/* Float: AI Triage */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
              style={{ position: "absolute", right: "-1rem", bottom: "5rem", width: "250px", borderRadius: "18px", background: t.floatBg, backdropFilter: "blur(14px)", border: `1px solid ${t.floatBorder}`, boxShadow: t.floatShadow, padding: "12px 14px", animation: "floatY 7s ease-in-out 1s infinite", transition: "background 0.3s, border-color 0.3s" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "38px", height: "38px", borderRadius: "12px", background: "linear-gradient(135deg,#f5a623,#f0c040)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Sparkles size={16} color="#7a4500" />
                </div>
                <div>
                  <p style={{ fontSize: "10px", color: t.floatSubText, margin: 0 }}>AI Triage</p>
                  <p style={{ fontSize: "11.5px", fontWeight: 600, color: t.floatTitleText, margin: 0 }}>Symptom check complete</p>
                </div>
              </div>
              <div style={{ marginTop: "10px", height: "5px", borderRadius: "9999px", background: t.progressBg }}>
                <div style={{ height: "100%", width: "80%", borderRadius: "9999px", background: t.progressFill }} />
              </div>
              <p style={{ marginTop: "6px", fontSize: "10px", color: t.floatSubText }}>Routed to General Physician in 38s</p>
            </motion.div>

            {/* Float: Patients */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              style={{ position: "absolute", bottom: "-1.5rem", left: "2rem", borderRadius: "18px", background: t.floatBg, backdropFilter: "blur(14px)", border: `1px solid ${t.floatBorder}`, boxShadow: t.floatShadow, padding: "10px 16px", display: "flex", alignItems: "center", gap: "10px", animation: "floatY 5.5s ease-in-out 0.5s infinite", transition: "background 0.3s, border-color 0.3s" }}
            >
              <div style={{ display: "flex" }}>
                {[1, 0.85, 0.7].map((op, i) => (
                  <div key={i} style={{ width: "28px", height: "28px", borderRadius: "9999px", border: `2px solid ${t.avatarBorder}`, background: t.avatarBg, opacity: op, marginLeft: i > 0 ? "-8px" : 0 }} />
                ))}
              </div>
              <div>
                <p style={{ fontSize: "12px", fontWeight: 600, color: t.floatPatientText, margin: 0 }}> patients</p>
                <p style={{ fontSize: "10px", color: t.floatSubText, margin: 0 }}>consulted today</p>
              </div>
            </motion.div>
          </div>
        </div>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

          @keyframes pulse        { 0%,100%{opacity:1} 50%{opacity:0.4} }
          @keyframes floatY       { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
          @keyframes marquee      { from{transform:translateX(0)} to{transform:translateX(-50%)} }
          @keyframes doxGlowPulse {
            0%   { transform: scale(1);    opacity: 0.55; }
            70%  { transform: scale(1.12); opacity: 0; }
            100% { transform: scale(1.12); opacity: 0; }
          }
          @keyframes doxDotBlink  {
            0%, 100% { opacity: 1;    transform: scale(1); }
            50%      { opacity: 0.35; transform: scale(0.85); }
          }
          @keyframes doxSlideUp   { from{transform:translateY(24px) scale(0.97);opacity:0} to{transform:translateY(0) scale(1);opacity:1} }
          @keyframes doxTyping    { 0%,80%,100%{transform:scale(0);opacity:0.3} 40%{transform:scale(1);opacity:1} }

          /* ── DoxBot floating button ── */
          .dox-fab-pill {
            transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease;
          }
          .dox-fab-pill:hover {
            transform: translateY(-3px) scale(1.03);
          }
          .doxGlow {
            position: absolute;
            inset: -3px;
            border-radius: 9999px;
            pointer-events: none;
            animation: doxGlowPulse 2.2s ease-out infinite;
            z-index: -1;
          }
          .dox-dot-blink {
            animation: doxDotBlink 1.6s ease-in-out infinite;
          }

          /* ── DESKTOP (default) ── */
          .hero-grid {
            position: relative;
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 1.5rem;
            display: grid;
            grid-template-columns: 1.1fr 1fr;
            gap: 3rem;
            align-items: center;
          }
          .hero-h1   { font-size: clamp(2.6rem, 4.5vw, 4rem); }
          .hero-desc { font-size: 1rem; max-width: 460px; }

          /* Search row: horizontal on desktop */
          .search-row     { display: flex; align-items: center; }
          .search-field   { display: flex; align-items: center; gap: 8px; flex: 1; padding: 11px 14px; }
          .search-field--city { flex: 0.75; }
          .search-input   { width: 100%; font-size: 13px; }
          .search-bottom-row {
            display: flex;
            align-items: center;
            border-left: none;
          }
          .search-field--date { padding: 11px 14px; flex-shrink: 0; border-right: none; }
          .search-btn {
            border: none;
            padding: 11px 20px;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            white-space: nowrap;
            flex-shrink: 0;
            transition: opacity .2s;
          }

          /* ── MOBILE ── */
          @media (max-width: 767px) {
            /* Single column, no overflow */
            .hero-grid {
              grid-template-columns: 1fr;
              gap: 2rem;
              padding: 0 1rem;
            }

            /* Hide the doctor image + floating cards entirely */
            .hero-right { display: none; }

            /* Smaller heading */
            .hero-h1 {
              font-size: clamp(2rem, 8vw, 2.8rem);
              letter-spacing: -1px;
            }
            .hero-desc {
              font-size: 0.9rem;
              max-width: 100%;
            }

            /* Search fields stack vertically */
            .search-row {
              flex-direction: column;
              align-items: stretch;
            }
            .search-field {
              border-right: none !important;
              border-bottom: 1px solid var(--dox-search-divider, #e2eceb);
              flex: unset;
              padding: 12px 14px;
            }
            .search-field--city { flex: unset; }

            /* Date + Find button side by side on mobile */
            .search-bottom-row {
              display: flex;
              flex-direction: row;
              align-items: center;
            }
            .search-field--date {
              flex: 1;
              border-bottom: none;
            }
            .search-btn {
              border-radius: 0 0 12px 0;
              padding: 13px 18px;
            }

            /* Section padding reduce */
            section#top {
              padding-top: 5rem !important;
              padding-bottom: 3rem !important;
            }
          }
        `}</style>
      </section>

      <DoxBot isDark={isDark} t={t} />
    </>
  );
}

export default Hero;